import React from "react";
import Meta from "../Meta";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import Loader from "../loader";
import renderIcon from "../../../components/renderIcon";
import HebcalService from "../../../services/hebcal";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const styles = `
.t26-horaires-page {
  padding: 0 clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px);
  max-width: var(--container);
  margin-inline: auto;
}

.t26-horaires-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: clamp(14px, 2vw, 20px);
}

.t26-horaires-card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  display: flex; flex-direction: column;
  position: relative;
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
}
.t26-horaires-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 30%, var(--line));
}

/* Jour courant : accentué */
.t26-horaires-card--today {
  border-color: var(--primary);
  box-shadow:
    0 0 0 2px color-mix(in oklab, var(--primary) 12%, transparent),
    var(--shadow-md);
}

/* Header de la card : nom du jour + numéro de date */
.t26-horaires-card__head {
  padding: 16px 18px 14px;
  background:
    radial-gradient(80% 100% at 100% 0%, color-mix(in oklab, var(--secondary) 14%, transparent) 0%, transparent 70%),
    color-mix(in oklab, var(--primary) 6%, var(--surface));
  border-bottom: 1px solid var(--line);
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px;
}
.t26-horaires-card--today .t26-horaires-card__head {
  background:
    radial-gradient(80% 100% at 100% 0%, color-mix(in oklab, var(--secondary) 28%, transparent) 0%, transparent 70%),
    color-mix(in oklab, var(--primary) 14%, var(--surface));
}
.t26-horaires-card__day {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700;
  color: var(--ink);
  text-transform: capitalize;
  line-height: 1.1;
}
.t26-horaires-card__date-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-pill);
  font-size: 12px; font-weight: 600;
  color: var(--ink-3);
}
.t26-horaires-card--today .t26-horaires-card__date-pill {
  background: var(--primary);
  color: var(--primary-contrast);
  border-color: var(--primary);
}

/* Badge "aujourd'hui" */
.t26-horaires-card__today-tag {
  position: absolute;
  top: 14px;
  inset-inline-end: 14px;
  background: var(--secondary);
  color: var(--secondary-contrast);
  font-size: 10px; font-weight: 700;
  letter-spacing: .14em; text-transform: uppercase;
  padding: 4px 10px;
  border-radius: var(--r-pill);
  z-index: 2;
}

/* Liste des horaires du jour */
.t26-horaires-card__list {
  list-style: none; margin: 0; padding: 0;
  flex: 1;
}
.t26-horaires-card__row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 18px;
  border-bottom: 1px dashed var(--line);
  gap: 12px;
}
.t26-horaires-card__row:last-child { border-bottom: 0; }
.t26-horaires-card__row-name {
  font-size: 14px; color: var(--ink);
  display: inline-flex; align-items: center; gap: 8px;
}
.t26-horaires-card__row-name svg {
  color: var(--primary);
  flex-shrink: 0;
}
.t26-horaires-card__row-time {
  font-family: var(--font-display);
  font-size: 16px; font-weight: 700;
  color: var(--primary);
  font-variant-numeric: tabular-nums;
  letter-spacing: .01em;
  flex-shrink: 0;
}

.t26-horaires-card__empty {
  padding: 24px 18px;
  font-size: 13px;
  color: var(--ink-3);
  text-align: center;
}

/* Vue mobile : sur une colonne */
@media (max-width: 560px) {
  .t26-horaires-grid { grid-template-columns: 1fr; }
}

.t26-horaires-empty {
  text-align: center;
  padding: clamp(60px, 10vw, 100px) 20px;
  color: var(--ink-3);
}
.t26-horaires-empty svg {
  color: color-mix(in oklab, var(--primary) 50%, transparent);
  opacity: .6;
  margin-bottom: 16px;
}
.t26-horaires-empty__title {
  font-family: var(--font-display);
  font-size: 22px; font-weight: 700;
  color: var(--ink);
  margin: 0 0 8px;
}
.t26-horaires-empty__sub {
  font-size: 15px;
  max-width: 44ch;
  margin-inline: auto;
}
`;

// Helpers temps
function formatTime(h, m, locale) {
    const d = new Date();
    d.setHours(h ?? 0, m ?? 0, 0, 0);
    return d.toLocaleTimeString(locale || "fr", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Construit la grille hebdomadaire (7 jours) avec leurs horaires.
 * Reprend la logique de Ummah/Horaires/horaires.js mais sans dépendance au
 * composant Timeline d'Ummah. Résultat : `[{dow, date, items:[{name, heure, minute}]}]`.
 */
function buildWeek(org) {
    if (!org || !Array.isArray(org.horaires) || org.horaires.length === 0) return [];

    const hebcal = new HebcalService(org, new Date(), org.lat, org.long);
    hebcal.generateHebCal();

    const byDow = {};
    for (const h of org.horaires) {
        if (!h.dow) continue;
        h.dow.forEach((dow) => {
            if (!byDow[dow]) byDow[dow] = [];

            if (h.fixed) {
                byDow[dow].push({ name: h.name, heure: h.heure, minute: h.minute });
                return;
            }

            // Zman variable : on calcule à partir d'Hebcal + offsets éventuels
            const refDate = new Date();
            refDate.setDate((refDate.getDate() - refDate.getDay()) + parseInt(dow, 10));
            const zManMs = hebcal.getVariableZman(h.zeman, refDate);
            if (!zManMs) return;

            const hz = new Date(zManMs);
            if (h.avant) hz.setMinutes(hz.getMinutes() - (h.minute || 0));
            else hz.setMinutes(hz.getMinutes() + (h.minute || 0));

            if (h.round) {
                const reste = hz.getMinutes() % 5;
                if (reste !== 0 && reste < 2.5) hz.setMinutes(hz.getMinutes() - reste);
                else if (reste !== 0) hz.setMinutes(hz.getMinutes() - reste + 5);
            }

            // Heure murale dans le fuseau de l'org (pas celui du serveur/navigateur)
            const wc = hebcal.getWallClock(hz) || { heure: hz.getHours(), minute: hz.getMinutes() };
            byDow[dow].push({ name: h.name, heure: wc.heure, minute: wc.minute });
        });
    }

    const sorted = [];
    for (const dow of Object.keys(byDow).sort()) {
        const d = new Date();
        d.setDate((d.getDate() - d.getDay()) + parseInt(dow, 10));
        sorted.push({
            dow,
            date: d,
            items: byDow[dow].sort((a, b) => {
                const ta = (a.heure || 0) * 60 + (a.minute || 0);
                const tb = (b.heure || 0) * 60 + (b.minute || 0);
                return ta - tb;
            }),
        });
    }
    return sorted;
}

export default function Horaires({ context, loading }) {
    const { org, langValue } = context;
    const locale = langValue || "fr";

    if (loading) {
        return (
            <>
                <Meta context={context} title={t`Horaires de ${org.name}`} />
                <Loader />
            </>
        );
    }

    const week = buildWeek(org);
    const todayDow = new Date().getDay(); // 0 = dimanche

    return (
        <>
            <Meta
                context={context}
                title={t`Horaires de ${org.name}`}
                description={t`Tous les horaires hebdomadaires de ${org.name}`}
            />
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <PageHeader
                title={t`Horaires`}
                titleEm={t`de la semaine`}
                subtitle={t`Retrouvez l'ensemble des horaires fixes et variables, du dimanche au samedi`}
                crumbs={[
                    { label: t`Accueil`, url: org.BASE_WEB_URL },
                    { label: t`Horaires` },
                ]}
            />

            <div className="t26-horaires-page">
                {week.length === 0 ? (
                    <EmptyState
                        icon="IconCalendarClock"
                        title={t`Bientôt disponible`}
                        subtitle={t`Les horaires de cette communauté n'ont pas encore été communiqués. Revenez bientôt.`}
                    />
                ) : (
                    <div className="t26-horaires-grid">
                        {week.map(day => {
                            const isToday = parseInt(day.dow, 10) === todayDow;
                            const dayName = day.date.toLocaleDateString(locale, { weekday: "long" });
                            const dayNumber = day.date.toLocaleDateString(locale, { day: "numeric", month: "short" }).replace(".", "");

                            return (
                                <div key={day.dow} className={`t26-horaires-card${isToday ? " t26-horaires-card--today" : ""}`}>
                                    {isToday && (
                                        <span className="t26-horaires-card__today-tag">
                                            <Trans>Aujourd'hui</Trans>
                                        </span>
                                    )}
                                    <div className="t26-horaires-card__head">
                                        <span className="t26-horaires-card__day">{dayName}</span>
                                        <span className="t26-horaires-card__date-pill">{dayNumber}</span>
                                    </div>

                                    {day.items.length === 0 ? (
                                        <div className="t26-horaires-card__empty">
                                            <Trans>Aucun horaire ce jour</Trans>
                                        </div>
                                    ) : (
                                        <ul className="t26-horaires-card__list">
                                            {day.items.map((it, i) => (
                                                <li key={i} className="t26-horaires-card__row">
                                                    <span className="t26-horaires-card__row-name">
                                                        {renderIcon({ name: "IconClock", source: "tabler" }, 14)}
                                                        {it.name}
                                                    </span>
                                                    <span className="t26-horaires-card__row-time">
                                                        {formatTime(it.heure, it.minute, locale)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}
